import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";

import { BACKEND_URL } from '../utils/apiBase';
const API = `${BACKEND_URL}/api`;

const BracketSetupPage = () => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [battles, setBattles] = useState([
    { match: 1, athlete1: null, athlete2: null },
    { match: 2, athlete1: null, athlete2: null },
    { match: 3, athlete1: null, athlete2: null },
    { match: 4, athlete1: null, athlete2: null },
    { match: 5, athlete1: null, athlete2: null },
    { match: 6, athlete1: null, athlete2: null },
    { match: 7, athlete1: null, athlete2: null },
    { match: 8, athlete1: null, athlete2: null }
  ]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [athletesRes, tournamentRes] = await Promise.all([
        axios.get(`${API}/athletes`),
        axios.get(`${API}/tournament/active`)
      ]);
      setAthletes(athletesRes.data);
      setTournament(tournamentRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const updateBattle = (index, field, value) => {
    const newBattles = [...battles];
    newBattles[index][field] = value;
    setBattles(newBattles);
  };

  const getUsedAthletes = () => {
    const used = [];
    battles.forEach(battle => {
      if (battle.athlete1) used.push(battle.athlete1);
      if (battle.athlete2) used.push(battle.athlete2);
    });
    return used;
  };

  const isAthleteAvailable = (athleteId, currentBattleIndex, currentField) => {
    const used = getUsedAthletes();
    const currentBattle = battles[currentBattleIndex];
    const isUsedElsewhere = used.filter(id => id === athleteId).length > 0;
    const isCurrentSelection = currentBattle[currentField] === athleteId;
    return !isUsedElsewhere || isCurrentSelection;
  };

  const createBracket = async () => {
    const incomplete = battles.some(b => !b.athlete1 || !b.athlete2);
    if (incomplete) {
      toast.error("All matches must have 2 athletes");
      return;
    }

    const used = getUsedAthletes();
    if (used.length !== new Set(used).size) {
      toast.error("Each athlete can only appear once");
      return;
    }

    if (used.length !== 16) {
      toast.error("TOP16 bracket requires exactly 16 athletes");
      return;
    }

    try {
      await axios.post(`${API}/tournament/bracket`, {
        tournament_id: tournament.id,
        battles: battles.map(b => ({
          athlete1_id: b.athlete1,
          athlete2_id: b.athlete2
        }))
      });
      toast.success("Bracket créé avec succès!");
      navigate("/director");
    } catch (error) {
      toast.error("Erreur lors de la création du bracket");
    }
  };

  const getAthleteById = (id) => athletes.find(a => a.id === id);

  return (
    <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #0f0f10 0%, #1a1625 100%)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/director")}
              data-testid="back-to-director"
            >
              <ArrowLeft size={20} className="mr-2" /> Retour
            </Button>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <span className="gradient-text">Configuration Bracket TOP16</span>
            </h1>
          </div>
          <Button
            onClick={createBracket}
            size="lg"
            data-testid="create-bracket-btn"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: 'white'
            }}
          >
            <Check size={20} className="mr-2" /> Create Bracket
          </Button>
        </div>

        <Card className="p-6 glass mb-6">
          <p className="text-gray-400">
            Définissez les 8 matchs du premier tour (TOP16). Les gagnants passeront automatiquement au tour suivant.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Athlètes sélectionnés: {getUsedAthletes().length}/16
          </p>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {battles.map((battle, index) => (
            <Card key={index} className="p-6 glass" data-testid={`battle-setup-${index + 1}`}>
              <h3 className="text-xl font-bold mb-4">Match {battle.match}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Athlète 1</label>
                  <Select
                    value={battle.athlete1 || ""}
                    onValueChange={(value) => updateBattle(index, 'athlete1', value)}
                  >
                    <SelectTrigger data-testid={`match-${index + 1}-athlete1`}>
                      <SelectValue placeholder="Select an athlete">
                        {battle.athlete1 && getAthleteById(battle.athlete1)?.name}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {athletes.filter(a => a && a.id && isAthleteAvailable(a.id, index, 'athlete1')).map(athlete => (
                        <SelectItem key={athlete.id} value={athlete.id}>
                          {athlete.name} ({athlete.country})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-center text-2xl font-bold text-purple-500">VS</div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Athlète 2</label>
                  <Select
                    value={battle.athlete2 || ""}
                    onValueChange={(value) => updateBattle(index, 'athlete2', value)}
                  >
                    <SelectTrigger data-testid={`match-${index + 1}-athlete2`}>
                      <SelectValue placeholder="Select an athlete">
                        {battle.athlete2 && getAthleteById(battle.athlete2)?.name}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {athletes.filter(a => a && a.id && isAthleteAvailable(a.id, index, 'athlete2')).map(athlete => (
                        <SelectItem key={athlete.id} value={athlete.id}>
                          {athlete.name} ({athlete.country})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BracketSetupPage;
